'use client';

import { FormEvent, useMemo, useRef, useState } from 'react';
import { LuTriangleAlert, LuGlobe, LuLock, LuShieldCheck, LuUsers, LuWifi } from 'react-icons/lu';

import { Button } from '@core/ui/primitives/button';
import { Badge } from '@core/ui/primitives/badge';
import { InputGroup, InputGroupInput } from '@core/ui/primitives/input-group';
import { RadioGroup, RadioGroupItem } from '@core/ui/primitives/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core/ui/primitives/tabs';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { validateVaultTitle } from '@vaults/core/VaultSlugService';
import { useToast } from '@/core/ui/hooks/use-toast';

export type VaultModeValue = 'managed' | 'synced';
export type VisibilityValue = 'private' | 'protected' | 'org' | 'public';
export type AnnotationsFormatValue = 'jsonl' | 'json';

interface OrganizationVaultCreateWizardProps {
  organizationId: string | null;
  organizationSlug: string | null;
  existingSlugs: string[];
  onCancel: () => void;
  onCreated?: (vault: { id: string; slug: string; title: string; organizationId: string }) => void | Promise<void>;
  hasExistingCredentials?: boolean;
}

const VISIBILITY_OPTIONS: Array<{ value: VisibilityValue; label: string; hint: string; icon: typeof LuLock }> = [
  {
    value: 'private',
    label: 'Private',
    hint: 'Only invited members can see the vault.',
    icon: LuLock,
  },
  {
    value: 'protected',
    label: 'Protected',
    hint: 'Visible to any signed-in user.',
    icon: LuShieldCheck,
  },
  {
    value: 'org',
    label: 'Organization',
    hint: 'Anyone in the organization can read; invites handle editing rights.',
    icon: LuUsers,
  },
  {
    value: 'public',
    label: 'Public',
    hint: 'Visible to everyone with the link.',
    icon: LuGlobe,
  },
];

const RESERVED_SLUGS = new Set(['admin', 'api', 'create', 'new', 'vaults', 'system']);

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function OrganizationVaultCreateWizard({
  organizationId,
  organizationSlug,
  existingSlugs,
  onCancel,
  onCreated,
  hasExistingCredentials = false,
}: OrganizationVaultCreateWizardProps) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [visibility, setVisibility] = useState<VisibilityValue>('private');
  const [gitMode, setGitMode] = useState<VaultModeValue>('managed');
  const [repoUrl, setRepoUrl] = useState('');
  const authType: 'pat' = 'pat';
  const [secretRef, setSecretRef] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editCredentials, setEditCredentials] = useState<boolean>(!hasExistingCredentials);
  const { toast } = useToast();

  const slugConflict = useMemo(() => {
    if (!slug) return false;
    return existingSlugs.includes(slug) || RESERVED_SLUGS.has(slug);
  }, [existingSlugs, slug]);

  const slugHint = slugConflict
    ? 'Slug unavailable – choose another.'
    : slug
    ? 'Slug looks good.'
    : 'Slug auto-generates from the vault name.';

  const derivedMode: VaultModeValue = gitMode;

  // Live form validity (used to enable/disable Create action)
  const isSynced = derivedMode === 'synced';
  const isNameValid = Boolean(title.trim()) && Boolean(slug) && !slugConflict && !validateVaultTitle(title);
  const requireSecret = isSynced && (editCredentials || !hasExistingCredentials);
  const hasExternalRepoFields = !isSynced || (Boolean(repoUrl.trim()) && (!requireSecret || Boolean(secretRef.trim())));
  const isFormValid = Boolean(organizationId) && isNameValid && hasExternalRepoFields;

  const handleTitleChange = (value: string) => {
    setTitle(value);
    setTitleError(validateVaultTitle(value));
    if (!slug || slug === slugify(title)) {
      setSlug(slugify(value));
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // These are guarded by isFormValid, but double-check for safety
    if (!organizationId || !title.trim() || !slug || slugConflict) {
      return;
    }

    if (gitMode === 'synced') {
      if (!repoUrl.trim() || (requireSecret && !secretRef.trim())) {
        return;
      }
    }

    setSubmitting(true);
    setCredentialError(null);
    setRepoError(null);

    try {
      const response = await fetch(`/api/organizations/${organizationId}/vaults`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim(),
          slug,
          visibility,
          mode: derivedMode,
          annotationsFormat: 'jsonl',
          defaultBranch: 'main',
          repoUrl: gitMode === 'synced' ? repoUrl.trim() : null,
          authType: gitMode === 'synced' && requireSecret ? authType : null,
          syncCredentialsSecretRef: gitMode === 'synced' && requireSecret ? secretRef.trim() : null,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        // Credential errors show under the input field
        if (payload?.code === 'CREDENTIALS_INVALID') {
          setCredentialError('Invalid credentials. The token may be expired or lack required permissions.');
          return;
        }
        if (payload?.code === 'REPO_ALREADY_LINKED') {
          const message =
            typeof payload?.message === 'string'
              ? payload.message
              : 'This repository is already linked to another vault.';
          setRepoError(message);
          return;
        }
        // All other API errors use toast
        const message = typeof payload?.error === 'string' ? payload.error : 'Failed to create vault.';
        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        });
        return;
      }

      const created = payload?.vault;
      if (created?.id && created?.organization?.id) {
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('vaults:updated'));
          }
        } catch {}
        await onCreated?.({
          id: created.id,
          slug: created.slug,
          title: created.title,
          organizationId: created.organization.id,
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create vault.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className='flex h-full flex-col'>
      <div className='flex-1 space-y-8 overflow-y-auto p-2'>
        {!organizationId ? (
          <div className='rounded-xl border border-dashed border-border/60 bg-muted/50 p-6 text-sm text-muted-foreground'>
            Select an organization first to create a vault.
          </div>
        ) : (
          <>
            <section className='grid gap-6 md:grid-cols-2'>
              <div className='md:col-span-2 space-y-2'>
                <label className='text-sm font-medium text-foreground'>Vault name</label>
                <InputGroup className={cn(titleError && 'border-destructive')}>
                  <InputGroupInput
                    value={title}
                    onChange={(event) => handleTitleChange(event.target.value)}
                    placeholder='Customer knowledge base'
                    aria-label='Vault name'
                    aria-invalid={Boolean(titleError)}
                  />
                </InputGroup>
                {titleError && (
                  <p className='text-[11px] text-destructive pl-1'>{titleError}</p>
                )}
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                    Slug
                    <Badge variant={slugConflict ? 'destructive' : 'outline'}>{slug || 'auto-generated'}</Badge>
                  </div>
                  {slugConflict && <div className='text-xs text-destructive'>{slugHint}</div>}
                </div>
              </div>
            </section>

            <section className='space-y-4'>
              <div className='space-y-1'>
                <h3 className='text-sm font-semibold text-foreground'>Visibility</h3>
                <p className='text-xs text-muted-foreground'>Control who can discover and edit this vault.</p>
              </div>
              <RadioGroup
                value={visibility}
                onValueChange={(value) => setVisibility(value as VisibilityValue)}
                className='grid grid-cols-2 gap-3'>
                {VISIBILITY_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm transition hover:border-brand/50',
                      visibility === option.value && 'border-brand bg-brand/10'
                    )}>
                    <RadioGroupItem value={option.value} className='mt-0.5 [&_svg]:!fill-brand data-[state=checked]:border-brand/60 focus-visible:!ring-0 focus-visible:!border-brand' />
                    <span className='flex flex-1 flex-col gap-1'>
                      <span className='flex items-center gap-2 font-medium text-foreground'>
                        <option.icon className='h-4 w-4 opacity-80' />
                        {option.label}
                      </span>
                      <span className='text-xs text-muted-foreground'>{option.hint}</span>
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </section>

            <section className='space-y-4'>
              <Tabs
                value={gitMode}
                onValueChange={(value) => setGitMode(value as VaultModeValue)}
                className='space-y-4'>
                <div className='space-y-1'>
                  <h3 className='text-sm font-semibold text-foreground'>Git repository (optional)</h3>
                  <p className='text-xs text-muted-foreground'>
                    Link an existing Git remote if you already have documentation there.
                  </p>
                </div>
                <TabsList>
                  <TabsTrigger value='managed'>Managed without remote</TabsTrigger>
                  <TabsTrigger value='synced'>
                    <span className='inline-flex items-center gap-2'>
                      <LuWifi className='h-4 w-4' /> Link to Git repository
                    </span>
                  </TabsTrigger>
                </TabsList>
                <TabsContent value='managed' className='text-xs text-muted-foreground'>
                  We’ll create a fresh Git repository managed by Inovisum.
                </TabsContent>
                <TabsContent value='synced'>
                  <div className='space-y-4 rounded-xl border border-border bg-muted/40 p-4'>
                    <div className='space-y-2'>
                      <label className='text-xs font-medium text-foreground'>Repository URL</label>
                      <InputGroup className={cn(repoError && 'border-destructive')}>
                        <InputGroupInput
                          type='url'
                          value={repoUrl}
                          onChange={(event) => {
                            setRepoUrl(event.target.value);
                            setRepoError(null);
                          }}
                          placeholder='https://dev.azure.com/org/project/_git/repo'
                        />
                      </InputGroup>
                      {repoError ? (
                        <p className='text-[11px] text-destructive pl-1 flex items-start gap-1.5'>
                          <LuTriangleAlert className='h-3.5 w-3.5 mt-0.5 flex-shrink-0' />
                          <span>{repoError}</span>
                        </p>
                      ) : null}
                    </div>
                    {hasExistingCredentials && !editCredentials ? (
                      <div className='flex items-center justify-between rounded-lg border border-border bg-background p-3 text-xs'>
                        <div className='text-muted-foreground'>Using existing organization credentials.</div>
                        <Button size='sm' variant='secondary' onClick={() => setEditCredentials(true)}>
                          Update credentials
                        </Button>
                      </div>
                    ) : (
                      <div className='flex items-start gap-4'>
                        <div className='space-y-2 text-xs w-56 flex-shrink-0'>
                          <label className='font-medium text-foreground'>Auth type</label>
                          <input
                            className='h-9 w-full rounded-md border border-gray-200 bg-gray-100 px-3 text-sm text-muted-foreground shadow-sm dark:border-gray-700 dark:bg-gray-900'
                            value='Personal access token'
                            readOnly
                          />
                        </div>
                        <div className='space-y-2 text-xs flex-1 min-w-0'>
                          <label className='font-medium text-foreground'>Personal access token</label>
                          <InputGroup className={cn('h-9', credentialError && 'border-destructive')}>
                            <InputGroupInput
                              value={secretRef}
                              onChange={(event) => {
                                setSecretRef(event.target.value);
                                setCredentialError(null);
                              }}
                              placeholder='Paste PAT here'
                            />
                          </InputGroup>
                          {credentialError ? (
                            <p className='text-[11px] text-destructive pl-3 flex items-start gap-1.5'>
                              <LuTriangleAlert className='h-3.5 w-3.5 mt-0.5 flex-shrink-0' />
                              <span>{credentialError}</span>
                            </p>
                          ) : (
                            <p className='text-[11px] text-muted-foreground pl-3'>
                              Provide a PAT with at least read/write access to the repository.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </section>
          </>
        )}
      </div>

      {/* Form Footer */}
      <div className='flex shrink-0 items-center justify-end gap-2 border-t border-gray-200/80 bg-white px-4 py-3 dark:border-gray-800/80 dark:bg-gray-950'>
        <Button type='button' variant='ghost' onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type='submit' disabled={submitting || !isFormValid}>
          {submitting ? 'Creating…' : 'Create vault'}
        </Button>
      </div>
    </form>
  );
}

export default OrganizationVaultCreateWizard;
